import {
  Alert,
  AnimatedContentLoader,
  BusinessLogo,
  Button,
  CountrySelect,
  Input,
  ModalV2,
  Shimmer,
  UploadFilesButton
} from '@components'
import { VatPeriod } from '@constants'
import { useQueryParam } from '@hooks'
import { useBusinessContext } from '@context'
import {
  useBusinessIdentifierTypes,
  useBusinessIdentifiers,
  useBusinessInvoicingCountries
} from '@root/query/hooks'
import {
  BusinessIdentifier,
  BusinessIdentifierTypeOption,
  createBusinessIdentifier,
  deleteBusinessIdentifier,
  deleteLogo,
  fetchBusiness,
  IBusiness,
  updateBusiness,
  updateBusinessIdentifier,
  uploadLogo
} from '@query'
import { setAPIErrors } from '@utils'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { UseFormSetError, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import {
  FaBuilding,
  FaIdCard,
  FaMapMarkerAlt,
  FaPen,
  FaSave,
  FaTrashAlt,
  FaUser
} from 'react-icons/fa'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import styled from 'styled-components'
import { SettingsReadOnlyField } from '../components/SettingsReadOnlyField'
import { SettingsSubPageLayout } from '@pages/SettingsPage/components/SettingsSubPageLayout'
import { SettingsDataCard } from '@pages/SettingsPage/components/SettingsDataCard'
import { CreateTagPill } from '@components/TagPill'
import {
  isGermanBusinessCountry,
  isValidDeSellerContactPhone
} from '@pages/InvoicingPage/xrechnungDelivery'

const VAT_IDENTIFIER_TYPE = 'vat_code'

type BusinessIdentifierFormValue = {
  id?: number
  type: string
  value: string
}

type BusinessInfoFormValues = {
  name: string
}

type AddressFormValues = Pick<
  IBusiness,
  'business_street' | 'business_postal_code' | 'business_city' | 'business_country'
>
type ContactFormValues = Pick<IBusiness, 'contact_phone'>

const getBusinessInfoDefaultValues = (business?: IBusiness): BusinessInfoFormValues => ({
  name: business?.name || ''
})

const getIdentifierFormValues = (
  type: string,
  identifier?: BusinessIdentifier
): BusinessIdentifierFormValue => ({
  id: identifier?.id,
  type,
  value: identifier?.value || ''
})

const getCommonErrorMessage = (errors: any): string | null => {
  if (Array.isArray(errors) && typeof errors[0] === 'string') {
    return errors[0]
  }

  if (!errors || typeof errors !== 'object') return null

  const typeErrors = errors.type
  if (Array.isArray(typeErrors) && typeof typeErrors[0] === 'string') {
    return typeErrors[0]
  }

  if (typeof typeErrors === 'string') {
    return typeErrors
  }

  const nonFieldErrors = errors.non_field_errors
  if (Array.isArray(nonFieldErrors) && typeof nonFieldErrors[0] === 'string') {
    return nonFieldErrors[0]
  }

  const commonErrors = errors.common
  if (Array.isArray(commonErrors) && typeof commonErrors[0] === 'string') {
    return commonErrors[0]
  }

  if (typeof commonErrors === 'string') {
    return commonErrors
  }

  return null
}

const isObjectErrorPayload = (payload: unknown): payload is Record<string, any> =>
  Boolean(payload) && typeof payload === 'object' && !Array.isArray(payload)

const isVatObliged = (business?: IBusiness) =>
  business?.default_vat_period !== undefined && business.default_vat_period !== VatPeriod.NONE

const getDeepLinkIdentifierSelection = (
  business: IBusiness,
  businessIdentifiers: BusinessIdentifier[],
  identifierTypeOptions: BusinessIdentifierTypeOption[],
  suggestedMissingIdentifierTypes: BusinessIdentifierTypeOption[]
): { type: string; identifier?: BusinessIdentifier } | null => {
  const existingVatIdentifier = businessIdentifiers.find(
    identifier => identifier.type === VAT_IDENTIFIER_TYPE
  )
  const canSuggestVat =
    isVatObliged(business) &&
    identifierTypeOptions.some(option => option.value === VAT_IDENTIFIER_TYPE)

  if (!existingVatIdentifier && canSuggestVat) {
    return { type: VAT_IDENTIFIER_TYPE }
  }

  if (suggestedMissingIdentifierTypes.length > 0) {
    return { type: suggestedMissingIdentifierTypes[0].value }
  }

  if (businessIdentifiers.length > 0) {
    const firstIdentifier = businessIdentifiers[0]
    return { type: firstIdentifier.type, identifier: firstIdentifier }
  }

  if (identifierTypeOptions.length > 0) {
    return { type: identifierTypeOptions[0].value }
  }

  return null
}

export const BusinessSettings: React.FC = () => {
  const queryClient = useQueryClient()
  const { slug } = useBusinessContext()
  const [showInfoModal, setShowInfoModal] = useState(false)
  const [showIdentifierModal, setShowIdentifierModal] = useState(false)
  const [showAddressModal, setShowAddressModal] = useState(false)
  const [showContactModal, setShowContactModal] = useState(false)
  const [isSavingBusinessInfo, setIsSavingBusinessInfo] = useState(false)
  const [isSavingIdentifier, setIsSavingIdentifier] = useState(false)
  const [modal, setModal] = useQueryParam('modal')
  const identifiersCardRef = useRef<HTMLDivElement>(null)
  const [t] = useTranslation()
  const { data: businessInvoicingCountries } = useBusinessInvoicingCountries()
  const { data: identifierTypeOptions = [] } = useBusinessIdentifierTypes()
  const {
    data: businessIdentifiers = [],
    isLoading: isBusinessIdentifiersLoading,
    refetch: refetchBusinessIdentifiers
  } = useBusinessIdentifiers()

  const identifierTypeLabelMap = useMemo(
    () => new Map(identifierTypeOptions.map(option => [option.value, option.label])),
    [identifierTypeOptions]
  )
  const identifierTypeInfoMap = useMemo(
    () => new Map(identifierTypeOptions.map(option => [option.value, option.info])),
    [identifierTypeOptions]
  )
  const requiredIdentifierTypes = useMemo(
    () =>
      new Set(
        identifierTypeOptions.filter(option => option.required === true).map(option => option.value)
      ),
    [identifierTypeOptions]
  )

  const { data, isLoading } = useQuery({
    queryKey: [slug],
    queryFn: () => fetchBusiness({ slug }),
    refetchOnMount: false
  })
  const isGermanBusiness = isGermanBusinessCountry(data?.country)

  const basicInfoForm = useForm<BusinessInfoFormValues>({
    defaultValues: {
      name: ''
    }
  })
  const identifierForm = useForm<BusinessIdentifierFormValue>({
    defaultValues: {
      type: '',
      value: ''
    }
  })
  const addressForm = useForm<AddressFormValues>()
  const contactForm = useForm<ContactFormValues>()

  const suggestedMissingIdentifierTypes = useMemo(() => {
    const existingTypes = new Set(businessIdentifiers.map(identifier => identifier.type))
    const vatObliged = isVatObliged(data)

    return identifierTypeOptions.filter(
      option =>
        !existingTypes.has(option.value) && (option.value !== VAT_IDENTIFIER_TYPE || vatObliged)
    )
  }, [businessIdentifiers, data, identifierTypeOptions])

  const openIdentifierModal = (type: string, identifier?: BusinessIdentifier) => {
    identifierForm.reset(getIdentifierFormValues(type, identifier))
    setShowIdentifierModal(true)
  }

  useEffect(() => {
    if (modal === 'business-info') {
      setShowInfoModal(true)
      setModal(undefined)
      return
    }

    if (modal !== 'business-identifiers') {
      return
    }

    if (isBusinessIdentifiersLoading || !data) {
      return
    }

    const selection = getDeepLinkIdentifierSelection(
      data,
      businessIdentifiers,
      identifierTypeOptions,
      suggestedMissingIdentifierTypes
    )
    // Always clear the deep-link param after handling so XRechnung guidance
    // never leaves a stuck `modal=` with no follow-up action.
    setModal(undefined)

    if (selection) {
      openIdentifierModal(selection.type, selection.identifier)
      return
    }

    identifiersCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [
    modal,
    setModal,
    isBusinessIdentifiersLoading,
    data,
    businessIdentifiers,
    identifierTypeOptions,
    suggestedMissingIdentifierTypes
  ])

  useEffect(() => {
    if (!data || showInfoModal) {
      return
    }

    basicInfoForm.reset(getBusinessInfoDefaultValues(data))

    addressForm.reset({
      business_street: data.business_street,
      business_postal_code: data.business_postal_code,
      business_city: data.business_city,
      business_country: data.business_country
    })

    contactForm.reset({
      contact_phone: data.contact_phone
    })
  }, [data, showInfoModal])

  const updateMutation = useMutation<IBusiness, unknown, Partial<IBusiness>>({
    mutationFn: payload => updateBusiness({ slug }, payload),
    ...{
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['businesses'] }),
          queryClient.invalidateQueries({ queryKey: [slug] }),
          queryClient.invalidateQueries({ queryKey: [slug, 'features'] })
        ])
        toast.success(t('general.changesSaved'))
      }
    }
  })

  const deleteLogoMutation = useMutation({
    mutationFn: () => deleteLogo({ slug }),
    ...{
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: [slug] })
        await queryClient.invalidateQueries({ queryKey: ['businesses'] })
      }
    }
  })

  const saveChanges = async (
    payload: Partial<IBusiness>,
    setError: UseFormSetError<any>,
    onSuccess: () => void
  ) => {
    try {
      await updateMutation.mutateAsync(payload)
      onSuccess()
    } catch (error) {
      const { status, data } = (error as any) ?? {}
      if (status === 400) {
        setAPIErrors(data, setError)
      }
    }
  }

  const resetBusinessInfoForm = () => {
    basicInfoForm.reset(getBusinessInfoDefaultValues(data))
  }

  const submitBusinessInfo = async (values: BusinessInfoFormValues) => {
    const trimmedName = values.name?.trim()
    const isBusinessNameChanged = trimmedName !== (data?.name || '').trim()

    if (!isBusinessNameChanged) {
      setShowInfoModal(false)
      return
    }

    try {
      setIsSavingBusinessInfo(true)

      await updateBusiness({ slug }, { name: trimmedName })
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [slug] }),
        queryClient.invalidateQueries({ queryKey: ['businesses'] })
      ])

      setShowInfoModal(false)
      toast.success(t('general.changesSaved'))
    } catch (error) {
      const { status, data: errorData } = (error as any) ?? {}

      if (status === 400) {
        if (isObjectErrorPayload(errorData)) {
          setAPIErrors(errorData, basicInfoForm.setError)
        }
        return
      }

      toast.error(t('general.error'))
    } finally {
      setIsSavingBusinessInfo(false)
    }
  }

  const invalidateIdentifierQueries = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: [slug, 'businessIdentifiers'] }),
      queryClient.invalidateQueries({ queryKey: [slug] }),
      queryClient.invalidateQueries({ queryKey: ['businesses'] })
    ])

  const submitIdentifier = async (values: BusinessIdentifierFormValue) => {
    try {
      setIsSavingIdentifier(true)

      const type = values.type?.trim()
      const value = values.value?.trim()

      if (values.id) {
        await updateBusinessIdentifier({ slug, identifierId: values.id }, { type, value })
      } else {
        await createBusinessIdentifier({ slug }, { type, value })
      }

      await invalidateIdentifierQueries()
      await refetchBusinessIdentifiers()
      setShowIdentifierModal(false)
      toast.success(t('general.changesSaved'))
    } catch (error) {
      const { status, data: errorData } = (error as any) ?? {}

      if (status === 403) {
        toast.error(t('settings.businessIdentifiers.writeForbidden'))
        return
      }

      if (status === 400) {
        if (isObjectErrorPayload(errorData)) {
          setAPIErrors(errorData, identifierForm.setError)
        }
        const commonError = getCommonErrorMessage(errorData)
        if (commonError) {
          toast.error(commonError)
        }
        return
      }

      toast.error(t('general.error'))
    } finally {
      setIsSavingIdentifier(false)
      await queryClient.invalidateQueries({ queryKey: [slug, 'businessIdentifiers'] })
    }
  }

  const deleteIdentifier = async () => {
    const identifierId = identifierForm.getValues('id')
    if (!identifierId) {
      return
    }

    try {
      setIsSavingIdentifier(true)
      await deleteBusinessIdentifier({ slug, identifierId })
      await invalidateIdentifierQueries()
      await refetchBusinessIdentifiers()
      setShowIdentifierModal(false)
      toast.success(t('general.changesSaved'))
    } catch (error) {
      const { status } = (error as any) ?? {}
      if (status === 403) {
        toast.error(t('settings.businessIdentifiers.writeForbidden'))
        return
      }
      toast.error(t('general.error'))
    } finally {
      setIsSavingIdentifier(false)
      await queryClient.invalidateQueries({ queryKey: [slug, 'businessIdentifiers'] })
    }
  }

  const saveBusinessInfo = basicInfoForm.handleSubmit(submitBusinessInfo)
  const saveIdentifier = identifierForm.handleSubmit(submitIdentifier)

  const onBusinessInfoSubmit: React.FormEventHandler<HTMLFormElement> = event => {
    basicInfoForm.clearErrors()
    void saveBusinessInfo(event)
  }

  const onIdentifierSubmit: React.FormEventHandler<HTMLFormElement> = event => {
    identifierForm.clearErrors()
    void saveIdentifier(event)
  }

  const isBusinessInfoSubmitDisabled = isSavingBusinessInfo || basicInfoForm.formState.isSubmitting
  const isIdentifierSubmitDisabled = isSavingIdentifier || identifierForm.formState.isSubmitting

  const identifierModalType = identifierForm.watch('type')
  const identifierModalId = identifierForm.watch('id')
  const isEditingIdentifier = Boolean(identifierModalId)
  const identifierModalTypeLabel =
    identifierTypeLabelMap.get(identifierModalType) || identifierModalType
  const identifierModalHeader = isEditingIdentifier
    ? identifierModalTypeLabel
    : t('settings.businessIdentifiers.addType', { type: identifierModalTypeLabel })
  const canDeleteIdentifier =
    isEditingIdentifier && !requiredIdentifierTypes.has(identifierModalType)

  return (
    <SettingsSubPageLayout title={t('settings.titleInfo')}>
      <AnimatedContentLoader isLoading={isLoading}>
        <CardsWrapper>
          <SettingsDataCard
            icon={<FaBuilding />}
            title={t('settings.titleInfo')}
            action={
              <Button
                isSecondary
                size="sm"
                onClick={() => {
                  resetBusinessInfoForm()
                  setShowInfoModal(true)
                }}
              >
                {t('general.edit')}
              </Button>
            }
          >
            <LogoSection>
              <CardLogoWrapper>
                <BusinessLogo size={64} name={data?.name} src={data?.logo} />
              </CardLogoWrapper>
            </LogoSection>
            <SettingsReadOnlyField label={t('general.name')} value={data?.name} />
          </SettingsDataCard>

          <div ref={identifiersCardRef}>
            <SettingsDataCard icon={<FaIdCard />} title={t('settings.businessIdentifiers.title')}>
              {isBusinessIdentifiersLoading ? (
                <IdentifierLoading>
                  <Shimmer width="60%" height="1rem" />
                </IdentifierLoading>
              ) : (
                <>
                  {businessIdentifiers.length === 0 &&
                    suggestedMissingIdentifierTypes.length === 0 && (
                      <SettingsReadOnlyField
                        label={t('settings.businessIdentifiers.title')}
                        value={undefined}
                        placeholder={t('settings.businessIdentifiers.empty')}
                      />
                    )}

                  {businessIdentifiers.map(identifier => (
                    <IdentifierRowButton
                      key={identifier.id}
                      type="button"
                      onClick={() => openIdentifierModal(identifier.type, identifier)}
                    >
                      <SettingsReadOnlyField
                        label={identifierTypeLabelMap.get(identifier.type) || identifier.type}
                        value={identifier.value}
                      />
                      <EditHint aria-hidden>
                        <FaPen />
                      </EditHint>
                    </IdentifierRowButton>
                  ))}

                  {suggestedMissingIdentifierTypes.length > 0 && (
                    <SuggestedIdentifierActions>
                      {suggestedMissingIdentifierTypes.map(option => (
                        <CreateTagPill
                          key={option.value}
                          text={t('settings.businessIdentifiers.addType', {
                            type: option.label
                          })}
                          onClick={() => openIdentifierModal(option.value)}
                        />
                      ))}
                    </SuggestedIdentifierActions>
                  )}
                </>
              )}
            </SettingsDataCard>
          </div>

          <SettingsDataCard
            icon={<FaMapMarkerAlt />}
            title={t('settings.businessAddress')}
            action={
              <Button isSecondary size="sm" onClick={() => setShowAddressModal(true)}>
                {t('general.edit')}
              </Button>
            }
            banner={
              !data?.has_business_address ? (
                <Alert type="warning" description={t('settings.businessAddressAlert')} />
              ) : undefined
            }
          >
            <SettingsReadOnlyField
              label={t('general.address.street')}
              value={data?.business_street}
            />
            <SettingsReadOnlyField
              label={t('general.address.postalCode')}
              value={data?.business_postal_code}
            />
            <SettingsReadOnlyField label={t('general.address.city')} value={data?.business_city} />
            <SettingsReadOnlyField
              label={t('general.address.country')}
              value={data?.business_country}
            />
          </SettingsDataCard>

          <SettingsDataCard
            icon={<FaUser />}
            title={t('settings.contactDetails')}
            action={
              <Button isSecondary size="sm" onClick={() => setShowContactModal(true)}>
                {t('general.edit')}
              </Button>
            }
          >
            <SettingsReadOnlyField label={t('settings.contactPerson')} value={data?.owner_name} />
            <SettingsReadOnlyField label={t('settings.contactEmail')} value={data?.owner_email} />
            <SettingsReadOnlyField label={t('settings.contactPhone')} value={data?.contact_phone} />
          </SettingsDataCard>
        </CardsWrapper>
      </AnimatedContentLoader>

      <ModalV2
        header={t('settings.titleInfo')}
        isVisible={showInfoModal}
        handleOnClose={() => setShowInfoModal(false)}
        width={500}
        footer={
          <ModalFooter>
            <Button type="button" onClick={() => setShowInfoModal(false)}>
              {t('general.cancel')}
            </Button>
            <Button
              intent="success"
              icon={<FaSave />}
              type="submit"
              form="business-info-form"
              disabled={isBusinessInfoSubmitDisabled}
              showSpinner={isSavingBusinessInfo || basicInfoForm.formState.isSubmitting}
            >
              {t('general.save')}
            </Button>
          </ModalFooter>
        }
      >
        <form id="business-info-form" onSubmit={onBusinessInfoSubmit}>
          <LogoSection>
            <LogoWrapper>
              <BusinessLogo name={data?.name} src={data?.logo} />
            </LogoWrapper>

            <LogoActions>
              <UploadFilesButton
                type="button"
                fileTypes={['.png', '.jpg', '.jpeg']}
                size="sm"
                uploadFile={(file, options) =>
                  uploadLogo({ slug }, file, options?.signal).then(() => {
                    queryClient.invalidateQueries({ queryKey: ['businesses'] })
                    queryClient.invalidateQueries({ queryKey: [slug] })
                  })
                }
              >
                {t('settings.uploadLogo')}
              </UploadFilesButton>

              {data?.logo && (
                <Button
                  type="button"
                  intent="danger"
                  size="sm"
                  isSecondary
                  icon={<FaTrashAlt />}
                  onClick={() => deleteLogoMutation.mutate()}
                  showSpinner={deleteLogoMutation.isPending}
                >
                  {t('settings.deleteLogo')}
                </Button>
              )}
            </LogoActions>
          </LogoSection>

          <Input
            label={t('general.name')}
            required
            errors={basicInfoForm.formState.errors.name}
            disabled={isBusinessInfoSubmitDisabled}
            {...basicInfoForm.register('name', {
              required: { value: true, message: t('validation.required') }
            })}
          />
        </form>
      </ModalV2>

      <ModalV2
        header={identifierModalHeader}
        isVisible={showIdentifierModal}
        handleOnClose={() => setShowIdentifierModal(false)}
        width={500}
        footer={
          <ModalFooter>
            <ModalFooterStart>
              {canDeleteIdentifier && (
                <Button
                  type="button"
                  intent="danger"
                  isSecondary
                  disabled={isIdentifierSubmitDisabled}
                  onClick={() => void deleteIdentifier()}
                >
                  {t('general.delete')}
                </Button>
              )}
            </ModalFooterStart>
            <ModalFooterEnd>
              <Button type="button" onClick={() => setShowIdentifierModal(false)}>
                {t('general.cancel')}
              </Button>
              <Button
                intent="success"
                icon={<FaSave />}
                type="submit"
                form="business-identifier-form"
                disabled={isIdentifierSubmitDisabled}
                showSpinner={isSavingIdentifier || identifierForm.formState.isSubmitting}
              >
                {t('general.save')}
              </Button>
            </ModalFooterEnd>
          </ModalFooter>
        }
      >
        <form id="business-identifier-form" onSubmit={onIdentifierSubmit}>
          <Input
            label={identifierModalTypeLabel}
            info={identifierTypeInfoMap.get(identifierModalType)}
            required
            disabled={isIdentifierSubmitDisabled}
            errors={identifierForm.formState.errors.value}
            {...identifierForm.register('value', {
              required: { value: true, message: t('validation.required') }
            })}
          />
        </form>
      </ModalV2>

      <ModalV2
        header={t('settings.businessAddress')}
        isVisible={showAddressModal}
        handleOnClose={() => setShowAddressModal(false)}
        width={500}
        footer={
          <ModalFooter>
            <Button type="button" onClick={() => setShowAddressModal(false)}>
              {t('general.cancel')}
            </Button>
            <Button
              intent="success"
              icon={<FaSave />}
              type="submit"
              form="business-address-form"
              disabled={updateMutation.isPending}
              showSpinner={updateMutation.isPending}
            >
              {t('general.save')}
            </Button>
          </ModalFooter>
        }
      >
        <form
          id="business-address-form"
          onSubmit={addressForm.handleSubmit(values =>
            saveChanges(values, addressForm.setError, () => setShowAddressModal(false))
          )}
        >
          <Input
            label={t('general.address.street')}
            errors={addressForm.formState.errors.business_street}
            required
            {...addressForm.register('business_street', {
              required: { value: true, message: t('validation.required') }
            })}
          />
          <Input
            label={t('general.address.postalCode')}
            errors={addressForm.formState.errors.business_postal_code}
            required
            {...addressForm.register('business_postal_code', {
              required: { value: true, message: t('validation.required') }
            })}
          />
          <Input
            label={t('general.address.city')}
            errors={addressForm.formState.errors.business_city}
            required
            {...addressForm.register('business_city', {
              required: { value: true, message: t('validation.required') }
            })}
          />
          <CountrySelect
            label={t('general.address.country')}
            errors={addressForm.formState.errors.business_country}
            required
            countries={businessInvoicingCountries}
            {...addressForm.register('business_country', {
              required: { value: true, message: t('validation.required') }
            })}
          />
        </form>
      </ModalV2>

      <ModalV2
        header={t('settings.titleContact')}
        isVisible={showContactModal}
        handleOnClose={() => setShowContactModal(false)}
        width={500}
        footer={
          <ModalFooter>
            <Button type="button" onClick={() => setShowContactModal(false)}>
              {t('general.cancel')}
            </Button>
            <Button
              intent="success"
              icon={<FaSave />}
              type="submit"
              form="business-contact-form"
              disabled={updateMutation.isPending}
              showSpinner={updateMutation.isPending}
            >
              {t('general.save')}
            </Button>
          </ModalFooter>
        }
      >
        <form
          id="business-contact-form"
          onSubmit={contactForm.handleSubmit(values =>
            saveChanges(values, contactForm.setError, () => setShowContactModal(false))
          )}
        >
          <fieldset disabled>
            <Input label={t('settings.contactPerson')} value={data?.owner_name} />
            <Input label={t('settings.contactEmail')} value={data?.owner_email} />
          </fieldset>

          <Input
            label={t('settings.contactPhone')}
            errors={contactForm.formState.errors.contact_phone}
            {...contactForm.register('contact_phone', {
              validate: value => {
                if (!isGermanBusiness) {
                  return true
                }

                if (!value?.trim()) {
                  return t('validation.xrechnung.sellerContactPhoneRequired')
                }

                if (!isValidDeSellerContactPhone(value)) {
                  return t('validation.xrechnung.sellerContactPhoneInvalid')
                }

                return true
              }
            })}
          />
        </form>
      </ModalV2>
    </SettingsSubPageLayout>
  )
}

const CardsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md}rem;
`

const LogoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding: ${({ theme }) => theme.spacing.md}rem;
`

const LogoWrapper = styled.div`
  max-width: 240px;
`

const CardLogoWrapper = styled.div`
  max-width: 160px;
`

const LogoActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

const SuggestedIdentifierActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  padding: ${({ theme }) => theme.spacing.md}rem;
`

const IdentifierLoading = styled.div`
  padding: ${({ theme }) => theme.spacing.md}rem;
`

const IdentifierRowButton = styled.button`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  min-width: 0;
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`

const EditHint = styled.span`
  display: flex;
  flex-shrink: 0;
  padding-right: ${({ theme }) => theme.spacing.md}rem;
  color: ${({ theme }) => theme.colors.metalGray};
  opacity: 0;

  ${IdentifierRowButton}:hover &,
  ${IdentifierRowButton}:focus-visible & {
    opacity: 1;
  }

  @media (hover: none) {
    opacity: 1;
  }

  svg {
    width: ${({ theme }) => theme.fontSize.sm}rem;
    height: ${({ theme }) => theme.fontSize.sm}rem;
  }
`

const ModalFooter = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  justify-content: space-between;
  width: 100%;
`

const ModalFooterStart = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const ModalFooterEnd = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`
